import React, { useContext, useEffect } from "react";
import { FormProvider, useForm, UseFormProps } from "react-hook-form";
import RenderLabel from "./RenderLabel";
import RenderWidgets from "./RenderWigets";
import Button from "@/common/ui/Button";
import { useFormChangeWarning } from "@/hooks/useFormChangeWarning";
import { FormUIContext } from "@/context";

interface FormRenderProps {
  formConfig: any;
  handleFormSubmit: (data: any) => void;
  className?: string;
  formClassName?: string;
  // Use in the Modal
  handleCancel?: () => void;
  //render children
  children?: React.ReactNode;
  btnContainerClass?: string;
  defaultValues?: any;
  isAlert?: boolean;
  useFormConfig?: Partial<UseFormProps>;
  disabledWarning?: boolean;
}

interface SubmitButtonsProps {
  handleCancel?: () => void;
  isSubmitting: boolean;
  submitBtnText?: string;
  btnContainerClass?: string;
  cancelBtnText?: string;
  isAlert?: boolean;
  isDisabled?: boolean;
}

export const SubmitButtons: React.FC<SubmitButtonsProps> = React.memo(
  ({ handleCancel, isSubmitting, submitBtnText = "Submit", btnContainerClass = "", cancelBtnText = "Cancel", isAlert = false, isDisabled = false }) => {
    const { isFormLoading }: any = useContext(FormUIContext) || {};
    const containerClass = handleCancel ? "flex justify-end gap-4 w-full pt-4" : "mt-5 w-full";

    const handleCancelClick = React.useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        handleCancel?.();
      },
      [handleCancel]
    );

    const PrimaryButton = React.useMemo(
      () => (
        <Button
          variant={isAlert ? "outline-danger" : "primary"}
          size="small"
          isFullWidth={!handleCancel}
          disabled={isDisabled || isSubmitting || isFormLoading}
          isLoading={isSubmitting}
        >
          {submitBtnText}
        </Button>
      ),
      [handleCancel, isSubmitting, isFormLoading, submitBtnText, isDisabled]
    );

    const SecondaryButton = React.useMemo(
      () =>
        handleCancel && (
          <Button variant={isAlert ? "primary" : "secondary"} size="small" disabled={isSubmitting || isFormLoading} onClick={handleCancelClick}>
            {cancelBtnText}
          </Button>
        ),
      [handleCancel, cancelBtnText, isSubmitting, isFormLoading, handleCancelClick]
    );

    return (
      <div className={`${containerClass} ${btnContainerClass}`.trim()}>
        {isAlert ? (
          <>
            {PrimaryButton}
            {SecondaryButton}
          </>
        ) : (
          <>
            {SecondaryButton}
            {PrimaryButton}
          </>
        )}
      </div>
    );
  }
);

SubmitButtons.displayName = "SubmitButtons";

const FormRender: React.FC<FormRenderProps> = ({
  formConfig,
  handleFormSubmit,
  handleCancel,
  className = "p-6 shadow-form border-1",
  formClassName = "pt-6",
  children,
  btnContainerClass,
  defaultValues,
  isAlert,
  useFormConfig,
  disabledWarning,
}) => {
  const methods = useForm(useFormConfig ?? { mode: "all", reValidateMode: "onChange" });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty, isSubmitSuccessful },
  } = methods;

  useFormChangeWarning(isDirty, disabledWarning ? disabledWarning : isSubmitSuccessful);

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  // console.log({ W: methods.watch(), D: defaultValues });

  return (
    <div className={className}>
      {formConfig?.formName && <h2 className="font-bold text-2xl text-center text-text-label">{formConfig.formName}</h2>}
      <FormProvider {...methods}>
        <form className={`h-fit items-center md:items-start flex flex-col w-full gap-y-3 ${formClassName}`} onSubmit={handleSubmit(handleFormSubmit)}>
        {/* These two files added to hide AutoFill Fields */}
          <input
            type="text"
            name="fakeusernameremembered"
            autoComplete="username"
            style={{ display: "none" }}
          />
          <input
            type="password"
            name="fakepasswordremembered"
            autoComplete="new-password"
            style={{ display: "none" }}
          />
          {/* --------------------------- */}
          {formConfig.sections.map((section: any, sectionIndex: number) => (
            <div className="flex flex-wrap w-full gap-x-30 gap-y-4" key={sectionIndex}>
              {section.widgets?.map((widget: any) => (
                <div className={`flex flex-col ${formConfig?.widgetsClass ? formConfig.widgetsClass : "w-full"}  gap-1`} key={widget?.field}>
                  {formConfig?.showLabel && !widget?.hideLabel && (
                    <RenderLabel label={widget.label} required={widget?.validation?.required} labelInfo={widget?.labelInfo} className="text-nowrap " />
                  )}
                  <RenderWidgets widget={widget} />
                </div>
              ))}
            </div>
          ))}
          {children && children}
          {/* submit Button */}
          <SubmitButtons
            handleCancel={handleCancel}
            isSubmitting={isSubmitting}
            submitBtnText={formConfig?.submitBtnText}
            cancelBtnText={formConfig?.cancelBtnText}
            btnContainerClass={btnContainerClass}
            isAlert={isAlert}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default FormRender;
